<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useUsersStore } from '@/views/Users/store'
import type { Comment } from '@/types'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Textarea } from '@/components/ui/textarea'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  Lock,
  Loader2,
} from 'lucide-vue-next'

const props = defineProps<{
  comment: Comment
}>()

const emit = defineEmits<{
  (e: 'edit', comment: Comment, content: string): void
  (e: 'delete', comment: Comment): void
}>()

const { t } = useI18n()
const authStore = useAuthStore()
const usersStore = useUsersStore()

const isEditing = ref(false)
const editContent = ref('')
const isSubmitting = ref(false)
const showDeleteDialog = ref(false)

const isAdmin = computed(() => authStore.user?.role === 'admin' || authStore.user?.role === 'operator')
const isOwner = computed(() => authStore.user?.id === props.comment.createdBy)
const isGuest = computed(() => !!props.comment.guestEmail)
const canEdit = computed(() => !isGuest.value && (isOwner.value || isAdmin.value))
const canDelete = computed(() => !isGuest.value && (isOwner.value || isAdmin.value))

const authorName = computed(() => {
  if (props.comment.guestEmail) {
    return props.comment.guestName || props.comment.guestEmail.split('@')[0]
  }
  const user = usersStore.users.find((u) => u.id === props.comment.createdBy)
  return user?.displayName || 'Usuario desconocido'
})

const authorInitials = computed(() => {
  const name = authorName.value
  const parts = name.split(' ')
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
})

const formattedDate = computed(() => {
  const date = props.comment.createdAt
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return 'Ahora'
  if (minutes < 60) return `Hace ${minutes} min`
  if (hours < 24) return `Hace ${hours}h`
  if (days < 7) return `Hace ${days}d`

  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  })
})

const wasEdited = computed(() => {
  return props.comment.updatedAt.getTime() - props.comment.createdAt.getTime() > 1000
})

const startEdit = () => {
  editContent.value = props.comment.content
  isEditing.value = true
}

const cancelEdit = () => {
  isEditing.value = false
  editContent.value = ''
}

const submitEdit = async () => {
  if (!editContent.value.trim()) return
  isSubmitting.value = true
  try {
    emit('edit', props.comment, editContent.value.trim())
    isEditing.value = false
  } finally {
    isSubmitting.value = false
  }
}

const confirmDelete = () => {
  emit('delete', props.comment)
  showDeleteDialog.value = false
}
</script>

<template>
  <div class="flex gap-3 py-3">
    <Avatar class="h-8 w-8">
      <AvatarFallback class="text-xs">{{ authorInitials }}</AvatarFallback>
    </Avatar>

    <div class="flex-1 min-w-0">
      <div class="flex items-center gap-2 mb-1">
        <span class="font-medium text-sm">{{ authorName }}</span>
        <span class="text-xs text-muted-foreground">{{ formattedDate }}</span>
        <Badge v-if="comment.isInternal" variant="secondary" class="text-xs">
          <Lock class="h-3 w-3 mr-1" />
          Interno
        </Badge>
        <Badge v-if="isGuest" variant="outline" class="text-xs">
          Externo
        </Badge>
        <span v-if="wasEdited" class="text-xs text-muted-foreground">(editado)</span>
      </div>

      <!-- Edit Mode -->
      <div v-if="isEditing" class="space-y-2">
        <Textarea
          v-model="editContent"
          class="min-h-[80px]"
          :disabled="isSubmitting"
        />
        <div class="flex gap-2">
          <Button size="sm" @click="submitEdit" :disabled="isSubmitting || !editContent.trim()">
            <Loader2 v-if="isSubmitting" class="mr-2 h-3 w-3 animate-spin" />
            Guardar
          </Button>
          <Button size="sm" variant="ghost" @click="cancelEdit" :disabled="isSubmitting">
            Cancelar
          </Button>
        </div>
      </div>

      <!-- View Mode -->
      <div v-else class="flex items-start gap-2">
        <p class="text-sm whitespace-pre-wrap flex-1">{{ comment.content }}</p>

        <DropdownMenu v-if="canEdit || canDelete">
          <DropdownMenuTrigger as-child>
            <Button variant="ghost" size="icon" class="h-6 w-6 shrink-0">
              <MoreHorizontal class="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem v-if="canEdit" @click="startEdit">
              <Pencil class="mr-2 h-4 w-4" />
              {{ t('common.edit') }}
            </DropdownMenuItem>
            <DropdownMenuItem
              v-if="canDelete"
              class="text-destructive"
              @click="showDeleteDialog = true"
            >
              <Trash2 class="mr-2 h-4 w-4" />
              {{ t('common.delete') }}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>

    <!-- Delete Dialog -->
    <AlertDialog v-model:open="showDeleteDialog">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{{ t('comments.deleteComment') }}</AlertDialogTitle>
          <AlertDialogDescription>
            {{ t('comments.deleteConfirm') }}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{{ t('common.cancel') }}</AlertDialogCancel>
          <AlertDialogAction
            @click="confirmDelete"
            class="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {{ t('common.delete') }}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>
